import React, { useState, useEffect } from 'react';
import { adsAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Megaphone, Plus, Edit2, Trash2, Eye, MousePointer } from 'lucide-react';
import { toast } from 'sonner';

const POSITIONS = [
  { value: 'header_banner', label: 'Header Banner' },
  { value: 'sidebar', label: 'Sidebar' },
  { value: 'in_article', label: 'In-Article' },
  { value: 'footer', label: 'Footer' },
  { value: 'interstitial', label: 'Interstitial' },
];

export default function AdminAds() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', position: 'sidebar', image_url: '', link_url: '', alt_text: '', is_active: true });

  useEffect(() => { loadAds(); }, []);

  const loadAds = async () => {
    setLoading(true);
    try { const res = await adsAPI.list({}); setAds(res.data.placements || []); }
    catch { toast.error('Failed to load ads'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    try {
      if (editing) { await adsAPI.update(editing.id, form); toast.success('Updated'); }
      else { await adsAPI.create(form); toast.success('Created'); }
      setShowDialog(false); setEditing(null);
      setForm({ name: '', position: 'sidebar', image_url: '', link_url: '', alt_text: '', is_active: true });
      loadAds();
    } catch { toast.error('Failed'); }
  };

  const deleteAd = async (id) => {
    try { await adsAPI.delete(id); toast.success('Deleted'); loadAds(); }
    catch { toast.error('Failed'); }
  };

  const openEdit = (ad) => {
    setEditing(ad);
    setForm({ name: ad.name, position: ad.position, image_url: ad.image_url || '', link_url: ad.link_url || '', alt_text: ad.alt_text || '', is_active: ad.is_active });
    setShowDialog(true);
  };

  return (
    <div className="space-y-6" data-testid="admin-ads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Megaphone className="w-6 h-6 text-[#B89B5E]" /> Ad Manager</h1>
          <p className="text-sm text-[#5B616E] mt-1">Manage ad placements and campaigns</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ name: '', position: 'sidebar', image_url: '', link_url: '', alt_text: '', is_active: true }); setShowDialog(true); }} data-testid="create-ad-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> New Placement</Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
      ) : ads.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {ads.map(ad => (
            <Card key={ad.id} className="bg-white border-[#E7E0D6]" data-testid="ad-card">
              <CardContent className="pt-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <Badge variant={ad.is_active ? 'default' : 'outline'} className={ad.is_active ? 'bg-[#1F7A5A] text-white text-[10px]' : 'text-[10px]'}>{ad.is_active ? 'Active' : 'Inactive'}</Badge>
                    <h3 className="font-medium mt-2">{ad.name}</h3>
                    <p className="text-xs text-[#5B616E] mt-0.5">{POSITIONS.find(p => p.value === ad.position)?.label || ad.position}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(ad)}><Edit2 className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#B3261E]" onClick={() => deleteAd(ad.id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#E7E0D6] text-xs text-[#5B616E]">
                  <div className="flex items-center gap-1"><Eye className="w-3 h-3" /> {ad.impressions || 0} impressions</div>
                  <div className="flex items-center gap-1"><MousePointer className="w-3 h-3" /> {ad.clicks || 0} clicks</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-[#5B616E]">
          <Megaphone className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>No ad placements configured</p>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Edit Placement' : 'New Ad Placement'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">Name</label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="ad-name-input" /></div>
            <div><label className="text-sm font-medium mb-1 block">Position</label>
              <Select value={form.position} onValueChange={v => setForm({ ...form, position: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{POSITIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Image URL</label><Input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} /></div>
            <div><label className="text-sm font-medium mb-1 block">Link URL</label><Input value={form.link_url} onChange={e => setForm({ ...form, link_url: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#121316] text-white hover:bg-[#1B1D22]">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
